/** x-test component */
import React, { useEffect, useState } from "react";

import { config } from "@app/config";
import { useCssVars } from "@app/hooks/use-css-vars";

import {
  SearchspringRecommendItem,
  useSearchspringRecommendResults,
} from "@app/hooks/use-searchspring-recommend-results";
import { useSearchspringResults } from "@app/hooks/use-searchspring-results";
import { SearchspringCollectionProvider, SearchspringRecommendProvider } from "@app/providers/searchspring";

import { CardProduct } from "@app/components/card-product";
import { viewItemDispatchEvent } from "@app/components/result-grid";

import { Variant } from "@app/types";

type ProductCompareProduct = {
  id: string;
  title: string;
  price: string;
  image: string;
  url: string;
  variants: Variant[];
};

type ProductCompare = {
  product: ProductCompareProduct;
  clone?: boolean;
  cta: {
    label: string;
    path?: string;
  };
  rows: { content: string; height: string; mobileHeight: string }[];
};

function CollectionCards() {
  const results = useSearchspringResults();
  if (!results.length) return null;

  return (
    <>
      {results.map((result, index) => {
        if (typeof result === "string") return null;
        else
          return (
            <div
              onClick={() => {
                viewItemDispatchEvent("Carousel", result, index);
              }}
              className="min-h-128 min-w-64 snap-start pb-2 md:min-w-96 lg:min-w-80"
            >
              <CardProduct source="searchspring" data={result} key={result.handle} />
            </div>
          );
      })}
    </>
  );
}

export const recommendationItemDispatchEvent = (
  source: "Carousel",
  item: SearchspringRecommendItem<any>,
  index: number,
) => {
  document.dispatchEvent(
    new CustomEvent("product:select_item", {
      detail: {
        source,
        index,
        variantId: item.uid,
        searchSearchVariantData: item,
      },
    }),
  );
};

function RecommendationsCards() {
  const results = useSearchspringRecommendResults();
  if (!results.length) return null;

  return (
    <>
      {results.map((result, index) => {
        if (typeof result === "string") return null;
        else
          return (
            <div
              onClick={() => {
                recommendationItemDispatchEvent("Carousel", result, index);
              }}
              className="min-h-128 min-w-64 snap-start pb-2 md:min-w-96 lg:min-w-80"
            >
              <CardProduct source="searchspring-recommdation" data={result} key={result.id} />
            </div>
          );
      })}
    </>
  );
}

const ProductCompareCardRow = ({ row }: { row: any }) => {
  if (!row.height) return null;
  const vars = useCssVars({
    "--input_height": `${row.height}rem`,
    "--input_mobile-height": `${row.mobileHeight}rem`,
  });

  return (
    <div
      style={vars}
      className="max-h-[--input_mobile-height] min-h-[--input_mobile-height] border-b py-4 md:max-h-[--input_height] md:min-h-[--input_height]"
    >
      <b className="md:hidden">{row.heading}</b>
      <div className="text-body-md richtext" dangerouslySetInnerHTML={{ __html: row.content ?? "-" }}></div>
    </div>
  );
};

function ProductCompareCards({ stringData }: { stringData?: string }) {
  const [results] = useState<ProductCompare[]>(() => {
    if (!stringData) return [];
    const data = JSON.parse(stringData);
    if (Array.isArray(data)) {
      // Used to close the last element for the mobile carousel.
      const lastEntry = data.pop();
      const clone = { ...lastEntry, clone: true };
      data.push(...[lastEntry, clone]);
    }
    return data;
  });

  const vars = useCssVars({
    "--third-split": "33%",
    "--half-split": `49%`,
  });

  return (
    <>
      {results.map((result, index) => {
        const url = !result?.cta?.path?.length ? result.product.url : result?.cta?.path;
        return (
          <div
            style={vars}
            data-ui={result?.clone ? "clone" : ""}
            className="flex flex-shrink-0 basis-3/4 snap-start flex-col ui-[clone]:invisible ui-[clone]:basis-1/4 md:basis-[--half-split] md:ui-[clone]:hidden xl:basis-[--third-split] 2xl:!min-w-[--third-split]"
          >
            <div className="h-full border-b pr-4 md:pr-6">
              <CardProduct source="shopify-section" data={result.product} key={index} />
            </div>
            {result.rows.map((row, index) => {
              return <ProductCompareCardRow row={row} />;
            })}
            <div className="flex min-h-20 justify-start py-4">
              <a href={url} className="btn btn-outline-light btn-sm md:btn-md h-fit w-fit p-3">
                {result.cta.label}
              </a>
            </div>
          </div>
        );
      })}
    </>
  );
}

export type Props = {
  config: typeof config;
  collectionHandle?: string;
  recommendationsProfile?: string;
  productCompare?: string;
};

export function ProductCards({ collectionHandle, recommendationsProfile, productSku, productCompare }: Props) {
  if (collectionHandle) {
    return (
      <SearchspringCollectionProvider category="products" handle={collectionHandle} resultsPerPage="24">
        <CollectionCards />
      </SearchspringCollectionProvider>
    );
  } else if (recommendationsProfile) {
    return (
      <SearchspringRecommendProvider
        category="products"
        tags={recommendationsProfile}
        resultsPerPage="24"
        products={productSku}
      >
        <RecommendationsCards />
      </SearchspringRecommendProvider>
    );
  } else if (productCompare) {
    return <ProductCompareCards stringData={productCompare} />;
  }

  return null;
}
